<script setup lang="ts">
import { computed, ref } from "vue";
import type { DashboardRedistributionActivityRow } from "../../features/dashboard/dashboard.types.ts";

const props = defineProps<{
  rows: DashboardRedistributionActivityRow[];
  paginationLabel: string;
}>();

const PAGE_SIZE = 5;
const currentPage = ref(1);

type SortField = keyof DashboardRedistributionActivityRow | null;
type SortDir = "asc" | "desc";

const sortField = ref<SortField>(null);
const sortDir = ref<SortDir>("asc");

function toggleSort(field: SortField): void {
  if (sortField.value === field) {
    sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDir.value = "asc";
  }
  currentPage.value = 1;
}

const sortedRows = computed(() => {
  if (!sortField.value) return props.rows;
  const field = sortField.value;
  return props.rows.toSorted((a, b) => {
    const av = a[field];
    const bv = b[field];
    let cmp = 0;
    cmp =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv));
    return sortDir.value === "asc" ? cmp : -cmp;
  });
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(sortedRows.value.length / PAGE_SIZE))
);

const pagedRows = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return sortedRows.value.slice(start, start + PAGE_SIZE);
});

function prevPage() {
  if (currentPage.value > 1) currentPage.value--;
}

function nextPage() {
  if (currentPage.value < totalPages.value) currentPage.value++;
}

function reasonClass(
  tone: DashboardRedistributionActivityRow["reasonTone"]
): string {
  switch (tone) {
    case "critical": {
      return "dashboard-status-badge--critical";
    }
    case "moderate": {
      return "dashboard-status-badge--moderate";
    }
    case "balanced": {
      return "dashboard-status-badge--balanced";
    }
  }
}
</script>

<template>
  <section class="dashboard-card rounded-[1.5rem] px-5 py-5">
    <div class="flex items-center justify-between gap-3">
      <h2 class="dashboard-text-primary text-lg font-semibold">
        Redistribution Activity
      </h2>
      <span class="dashboard-text-muted text-xs"
        >Recent movement snapshots</span
      >
    </div>

    <div class="mt-4 overflow-x-auto">
      <table
        class="dashboard-text-secondary min-w-full border-collapse text-left text-sm"
      >
        <thead>
          <tr
            class="dashboard-divider dashboard-text-muted border-b text-xs tracking-[0.14em] uppercase"
          >
            <th
              class="cursor-pointer pr-4 pb-3 font-semibold select-none hover:opacity-80"
              @click="toggleSort('sku')"
            >
              SKU
              <span class="sort-indicator" aria-hidden="true">
                {{
                  sortField === "sku" ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"
                }}
              </span>
            </th>
            <th
              class="cursor-pointer pr-4 pb-3 font-semibold select-none hover:opacity-80"
              @click="toggleSort('fromPurchaseOrder')"
            >
              From PO
              <span class="sort-indicator" aria-hidden="true">
                {{
                  sortField === "fromPurchaseOrder"
                    ? sortDir === "asc"
                      ? " ▲"
                      : " ▼"
                    : " ⇅"
                }}
              </span>
            </th>
            <th
              class="cursor-pointer pr-4 pb-3 font-semibold select-none hover:opacity-80"
              @click="toggleSort('fromDistributionCenter')"
            >
              From DC
              <span class="sort-indicator" aria-hidden="true">
                {{
                  sortField === "fromDistributionCenter"
                    ? sortDir === "asc"
                      ? " ▲"
                      : " ▼"
                    : " ⇅"
                }}
              </span>
            </th>
            <th
              class="cursor-pointer pr-4 pb-3 font-semibold select-none hover:opacity-80"
              @click="toggleSort('toPurchaseOrder')"
            >
              To PO
              <span class="sort-indicator" aria-hidden="true">
                {{
                  sortField === "toPurchaseOrder"
                    ? sortDir === "asc"
                      ? " ▲"
                      : " ▼"
                    : " ⇅"
                }}
              </span>
            </th>
            <th
              class="cursor-pointer pr-4 pb-3 font-semibold select-none hover:opacity-80"
              @click="toggleSort('toDistributionCenter')"
            >
              To DC
              <span class="sort-indicator" aria-hidden="true">
                {{
                  sortField === "toDistributionCenter"
                    ? sortDir === "asc"
                      ? " ▲"
                      : " ▼"
                    : " ⇅"
                }}
              </span>
            </th>
            <th
              class="cursor-pointer pr-4 pb-3 font-semibold select-none hover:opacity-80"
              @click="toggleSort('unitsMoved')"
            >
              Units
              <span class="sort-indicator" aria-hidden="true">
                {{
                  sortField === "unitsMoved"
                    ? sortDir === "asc"
                      ? " ▲"
                      : " ▼"
                    : " ⇅"
                }}
              </span>
            </th>
            <th
              class="cursor-pointer pb-3 font-semibold select-none hover:opacity-80"
              @click="toggleSort('reason')"
            >
              Reason
              <span class="sort-indicator" aria-hidden="true">
                {{
                  sortField === "reason"
                    ? sortDir === "asc"
                      ? " ▲"
                      : " ▼"
                    : " ⇅"
                }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in pagedRows"
            :key="`${row.sku}-${row.fromPurchaseOrder}-${row.toPurchaseOrder}-${row.reason}-${idx}`"
            class="border-b last:border-b-0"
            :style="{ borderColor: 'var(--dashboard-table-divider)' }"
          >
            <td class="dashboard-text-primary py-3 pr-4 font-medium">
              {{ row.sku }}
            </td>
            <td class="py-3 pr-4">{{ row.fromPurchaseOrder }}</td>
            <td class="py-3 pr-4">{{ row.fromDistributionCenter }}</td>
            <td class="py-3 pr-4">{{ row.toPurchaseOrder }}</td>
            <td class="py-3 pr-4">{{ row.toDistributionCenter }}</td>
            <td class="py-3 pr-4">{{ row.unitsMoved }}</td>
            <td class="py-3">
              <span
                class="dashboard-status-badge"
                :class="reasonClass(row.reasonTone)"
              >
                {{ row.reason }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      class="dashboard-divider dashboard-text-secondary mt-4 flex flex-col gap-3 border-t pt-4 text-sm sm:flex-row sm:items-center sm:justify-between"
    >
      <RouterLink
        :to="{ name: 'search-select-pos' }"
        class="dashboard-text-link inline-flex items-center gap-2 font-semibold no-underline"
      >
        View all movements
        <span aria-hidden="true">›</span>
      </RouterLink>
      <div class="flex items-center gap-4">
        <span>
          Showing {{ (currentPage - 1) * PAGE_SIZE + 1 }}&ndash;{{
            Math.min(currentPage * PAGE_SIZE, rows.length)
          }}
          of {{ rows.length }}
        </span>
        <span
          class="dashboard-text-primary inline-flex items-center gap-2 text-lg opacity-80"
        >
          <button
            type="button"
            aria-label="Previous page"
            :disabled="currentPage <= 1"
            class="cursor-pointer disabled:cursor-default disabled:opacity-40"
            @click="prevPage"
          >
            <span aria-hidden="true">‹</span>
          </button>
          <button
            type="button"
            aria-label="Next page"
            :disabled="currentPage >= totalPages"
            class="cursor-pointer disabled:cursor-default disabled:opacity-40"
            @click="nextPage"
          >
            <span aria-hidden="true">›</span>
          </button>
        </span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.sort-indicator {
  font-size: 10px;
  opacity: 0.6;
}
</style>
